/**
 * The platform's default disciplines, read-only, beside the tenant's own list.
 *
 * @module components/admin/DefaultDisciplinesPanel
 */

import React from "react";

import { DEFAULT_DISCIPLINES } from "@/components/context/TenantDisciplinesContext";
import { Badge, Button, countLabel, ListRow, MutedText, Panel, Text } from "@/components/ui";

/** Programmes per discipline name, from the tenant's programme summaries. */
export type DisciplineUsage = ReadonlyMap<string, number>;

/** "Used by 2 programmes", or nothing when usage is unknown. */
export function DisciplineUsageNote({
  usage,
  discipline,
  testIdPrefix,
}: {
  usage: DisciplineUsage | undefined;
  discipline: string;
  /** Per list, since a tenant list started from the defaults repeats their names. */
  testIdPrefix: "default" | "tenant";
}): React.ReactElement | null {
  if (!usage) {
    return null;
  }
  const count = usage.get(discipline) ?? 0;
  return (
    <MutedText as="span" data-testid={`${testIdPrefix}-discipline-usage-${discipline}`}>
      {count === 0 ? "Not used" : `Used by ${countLabel(count, "programme")}`}
    </MutedText>
  );
}

/** Whether the Identity step offers the defaults; unknown until the tenant list is read. */
export type DefaultsState = "in-use" | "replaced" | "unknown";

const STATE_TEXT: Record<
  Exclude<DefaultsState, "unknown">,
  { badge: string; description: string }
> = {
  "in-use": {
    badge: "In use",
    description: "Offered on every programme's Identity step until the tenant lists its own.",
  },
  replaced: {
    badge: "Replaced",
    description:
      "The tenant list replaces these. List any default the tenant still wants there too.",
  },
};

export interface DefaultDisciplinesPanelProps {
  state: DefaultsState;
  usage: DisciplineUsage | undefined;
  /** Why "Start from defaults" refuses, or undefined when it may run. */
  startRefusal: string | undefined;
  onStartFromDefaults: () => void;
}

export function DefaultDisciplinesPanel({
  state,
  usage,
  startRefusal,
  onStartFromDefaults,
}: DefaultDisciplinesPanelProps): React.ReactElement {
  const inUse = state === "in-use";
  const text = state === "unknown" ? undefined : STATE_TEXT[state];
  return (
    <Panel
      headingLevel={2}
      title="Platform defaults"
      data-testid="platform-default-disciplines"
      description={text?.description}
      actions={
        text ? (
          <Badge
            tone={inUse ? "success" : "neutral"}
            variant="soft"
            size="sm"
            data-testid="platform-default-disciplines-state"
          >
            {text.badge}
          </Badge>
        ) : null
      }
    >
      {/* The same divided list as the tenant's own, so the two read as one kind of thing. */}
      <ul className="divide-y divide-base-300 overflow-hidden rounded-box border border-base-300">
        {DEFAULT_DISCIPLINES.map((discipline) => (
          <li key={discipline}>
            <ListRow
              tone="inset"
              framed={false}
              className="items-center! py-2.5!"
              trailing={
                <DisciplineUsageNote usage={usage} discipline={discipline} testIdPrefix="default" />
              }
            >
              <Text
                as="span"
                tone={state === "replaced" ? "muted" : "default"}
                className={state === "replaced" ? "" : "font-medium"}
              >
                {discipline}
              </Text>
            </ListRow>
          </li>
        ))}
      </ul>
      {inUse ? (
        <Button
          className="mt-3"
          size="sm"
          variant="outline"
          icon="copy"
          disabled={Boolean(startRefusal)}
          disabledReason={startRefusal}
          onClick={onStartFromDefaults}
          data-testid="discipline-start-from-defaults"
        >
          Start from defaults
        </Button>
      ) : null}
    </Panel>
  );
}
